import { useEffect, useState } from "react";
import {
    fetchUserDirectory,
    fetchUserDirectoryDepartments,
    UserDirectoryItem,
} from "@/lib/axios/api/user-directory.api";

interface UseUserDirectoryParams {
    userName: string;
    departments: string[];
    page: number;
    pageSize: number;
    startTime?: string | null;
    endTime?: string | null;
    agentIds?: string[] | null;
}

interface UseUserDirectoryReturn {
    rows: UserDirectoryItem[];
    totalCount: number;
    loading: boolean;
    error: string | null;
    availableDepartments: string[];
    departmentsLoading: boolean;
}

export function useUserDirectory({
    userName,
    departments,
    page,
    pageSize,
    startTime,
    endTime,
    agentIds,
}: UseUserDirectoryParams): UseUserDirectoryReturn {
    const [rows, setRows] = useState<UserDirectoryItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [availableDepartments, setAvailableDepartments] = useState<string[]>([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        setDepartmentsLoading(true);
        fetchUserDirectoryDepartments({ signal: controller.signal })
            .then((data) => setAvailableDepartments(data))
            .catch((err) => {
                if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
                    console.error("Failed to load departments:", err);
                }
            })
            .finally(() => setDepartmentsLoading(false));

        return () => controller.abort();
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        fetchUserDirectory(
            {
                userName,
                departments: departments,
                page,
                pageSize,
                startTime,
                endTime,
                agentIds,
            },
            { signal: controller.signal }
        )
            .then((response) => {
                setRows(response.items ?? []);
                setTotalCount(response.totalCount ?? 0);
            })
            .catch((err) => {
                if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
                    setError(err instanceof Error ? err.message : "Failed to fetch user directory");
                    setRows([]);
                    setTotalCount(0);
                }
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, [userName, departments, page, pageSize, startTime, endTime, agentIds]);

    return {
        rows,
        totalCount,
        loading,
        error,
        availableDepartments,
        departmentsLoading,
    };
}
